/**
 * NumbersPane -- mini-spreadsheet for quantitative interview tasks.
 *
 * Use cases (per template the recruiter opted in via enable_numbers_panel):
 *   - Consulting case interviews: market sizing, profitability trees
 *     (Population x Penetration x ARPU x ...)
 *   - Finance: DCF / NPV / sensitivity
 *   - Sales: pipeline math (Deals x Avg-Size x Win-Rate)
 *   - Strategy: any back-of-envelope computation
 *
 * Each edit fires `onChange({ rows, cols, cells, summary, cell_count })`.
 * Parent owns debouncing via useArtifactSync.
 *
 * Submit button -> `onEvent('submit', { cell_count })`.
 *
 * Formula support (intentionally small subset):
 *   =A1+B1, =A1-B1, =A1*B1, =A1/B1, =A1^2  (basic arithmetic)
 *   =SUM(A1:A5)         column or row range
 *   =AVG(A1:A5)
 *   =MIN(A1:A5), =MAX(A1:A5)
 *   =COUNT(A1:A5)       counts non-empty numeric cells
 *   =NPV(rate, A1:A5)   net present value (rate as decimal)
 *   =PRODUCT(A1:A5)
 *   =IF(cond, then, else) where cond is e.g. A1>0
 *   nested arithmetic: =(A1+B1)*0.6
 *   number literals: 83000000, 0.6, -42.5
 *   text labels: any cell starting with non-= renders as string
 *
 * Evaluation strategy: simple recursive-descent parser (~150 lines), no eval().
 * Topological dependency resolution per recompute: cells reference cells, we
 * resolve in evaluation order with a visited-set to detect cycles
 * (cyclic cells get value `#CYCLE`).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Send, Eye, Trash2, Calculator } from 'lucide-react';

// =====================================================================
// Address helpers (A1 <-> [row, col])
// =====================================================================
function colLabel(idx: number): string {
  // 0 -> A, 25 -> Z, 26 -> AA
  let s = '';
  let n = idx;
  while (true) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
    if (n < 0) break;
  }
  return s;
}
function parseAddr(addr: string): { row: number; col: number } | null {
  const m = /^([A-Z]+)(\d+)$/.exec(addr.trim().toUpperCase());
  if (!m) return null;
  const colStr = m[1];
  const rowStr = m[2];
  let col = 0;
  for (const ch of colStr) {
    col = col * 26 + (ch.charCodeAt(0) - 64);
  }
  return { row: parseInt(rowStr, 10) - 1, col: col - 1 };
}

// =====================================================================
// Formula parser (minimal recursive-descent)
// =====================================================================
type CellValue = string | number | null;

interface EvalContext {
  rawCells: Record<string, string>;  // address -> raw input (without leading "=")
  resolved: Record<string, CellValue>;
  resolving: Set<string>;
}

function tokenize(expr: string): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < expr.length) {
    const c = expr[i];
    if (c === ' ' || c === '\t') { i++; continue; }
    if ('+-*/^(),:'.includes(c)) { out.push(c); i++; continue; }
    if (c === '>' || c === '<' || c === '=' || c === '!') {
      // comparison operator, possibly two chars (>=, <=, ==, !=)
      if (expr[i + 1] === '=') { out.push(c + '='); i += 2; continue; }
      out.push(c); i++; continue;
    }
    if (/[0-9.]/.test(c)) {
      let j = i;
      while (j < expr.length && /[0-9.eE+\-]/.test(expr[j])) {
        // allow negative exponents but not leading + or - that aren't part of e/E
        if ((expr[j] === '+' || expr[j] === '-') && j > i && expr[j - 1] !== 'e' && expr[j - 1] !== 'E') break;
        j++;
      }
      out.push(expr.slice(i, j));
      i = j;
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      let j = i;
      while (j < expr.length && /[A-Za-z0-9_]/.test(expr[j])) j++;
      out.push(expr.slice(i, j).toUpperCase());
      i = j;
      continue;
    }
    // unknown character — treat as terminator
    i++;
  }
  return out;
}

class Parser {
  private pos = 0;
  constructor(private tokens: string[], private ctx: EvalContext) {}
  parseExpr(): number {
    return this.parseComparison();
  }
  // comparison (lowest precedence above the top-level expression)
  private parseComparison(): number {
    let left = this.parseAddSub();
    while (this.pos < this.tokens.length) {
      const t = this.tokens[this.pos];
      if (['>', '<', '>=', '<=', '=', '==', '!='].includes(t)) {
        this.pos++;
        const right = this.parseAddSub();
        switch (t) {
          case '>': left = left > right ? 1 : 0; break;
          case '<': left = left < right ? 1 : 0; break;
          case '>=': left = left >= right ? 1 : 0; break;
          case '<=': left = left <= right ? 1 : 0; break;
          case '=':
          case '==': left = left === right ? 1 : 0; break;
          case '!=': left = left !== right ? 1 : 0; break;
        }
      } else {
        break;
      }
    }
    return left;
  }
  private parseAddSub(): number {
    let left = this.parseMulDiv();
    while (this.pos < this.tokens.length) {
      const t = this.tokens[this.pos];
      if (t === '+' || t === '-') {
        this.pos++;
        const right = this.parseMulDiv();
        left = t === '+' ? left + right : left - right;
      } else break;
    }
    return left;
  }
  private parseMulDiv(): number {
    let left = this.parsePow();
    while (this.pos < this.tokens.length) {
      const t = this.tokens[this.pos];
      if (t === '*' || t === '/') {
        this.pos++;
        const right = this.parsePow();
        left = t === '*' ? left * right : left / right;
      } else break;
    }
    return left;
  }
  private parsePow(): number {
    let left = this.parseUnary();
    while (this.pos < this.tokens.length && this.tokens[this.pos] === '^') {
      this.pos++;
      const right = this.parseUnary();
      left = Math.pow(left, right);
    }
    return left;
  }
  private parseUnary(): number {
    if (this.tokens[this.pos] === '-') { this.pos++; return -this.parseUnary(); }
    if (this.tokens[this.pos] === '+') { this.pos++; return this.parseUnary(); }
    return this.parsePrimary();
  }
  private parsePrimary(): number {
    const t = this.tokens[this.pos];
    if (t === undefined) throw new Error('unexpected end');
    // Parenthesized expression
    if (t === '(') {
      this.pos++;
      const v = this.parseExpr();
      if (this.tokens[this.pos] !== ')') throw new Error('missing )');
      this.pos++;
      return v;
    }
    // Number literal
    if (/^[0-9.]/.test(t) || (t.startsWith('-') && /[0-9]/.test(t[1] || ''))) {
      this.pos++;
      const n = Number(t);
      if (Number.isNaN(n)) throw new Error(`bad number: ${t}`);
      return n;
    }
    // Function call or cell ref
    if (/^[A-Z]/.test(t)) {
      const isFn = this.tokens[this.pos + 1] === '(';
      if (isFn) return this.parseFunction();
      // cell ref like A1, AB12
      this.pos++;
      return this.resolveCell(t);
    }
    throw new Error(`unexpected token: ${t}`);
  }
  private parseFunction(): number {
    const name = this.tokens[this.pos];
    this.pos++; // name
    if (this.tokens[this.pos] !== '(') throw new Error(`expected (`);
    this.pos++;
    const args: number[] = [];
    const ranges: { addrs: string[]; vals: number[] }[] = [];
    if (this.tokens[this.pos] !== ')') {
      while (true) {
        // peek for range A1:A5
        if (
          this.pos + 2 < this.tokens.length
          && /^[A-Z]+\d+$/.test(this.tokens[this.pos])
          && this.tokens[this.pos + 1] === ':'
          && /^[A-Z]+\d+$/.test(this.tokens[this.pos + 2])
        ) {
          const a = this.tokens[this.pos];
          const b = this.tokens[this.pos + 2];
          this.pos += 3;
          const addrs = expandRange(a, b);
          const vals: number[] = [];
          for (const addr of addrs) {
            const v = this.resolveCell(addr);
            if (typeof v === 'number' && !Number.isNaN(v)) vals.push(v);
          }
          ranges.push({ addrs, vals });
          // function args see the SUM of the range as a single arg too,
          // but for SUM/AVG/etc we use ranges directly via the ranges arr.
          // For NPV we want rate then range, so we DON'T flatten.
        } else {
          args.push(this.parseExpr());
        }
        if (this.tokens[this.pos] === ',') { this.pos++; continue; }
        break;
      }
    }
    if (this.tokens[this.pos] !== ')') throw new Error(`expected ) after ${name}`);
    this.pos++;
    return applyFunction(name, args, ranges);
  }
  private resolveCell(addr: string): number {
    if (this.ctx.resolving.has(addr)) {
      throw new Error(`cycle: ${addr}`);
    }
    if (this.ctx.resolved[addr] !== undefined) {
      const v = this.ctx.resolved[addr];
      if (typeof v === 'number') return v;
      if (v === null || v === '') return 0;
      const n = Number(v);
      return Number.isNaN(n) ? 0 : n;
    }
    const raw = this.ctx.rawCells[addr];
    if (raw === undefined || raw === '') {
      this.ctx.resolved[addr] = null;
      return 0;
    }
    return evaluateRaw(addr, raw, this.ctx);
  }
}

function expandRange(a: string, b: string): string[] {
  const A = parseAddr(a);
  const B = parseAddr(b);
  if (!A || !B) return [];
  const out: string[] = [];
  const r0 = Math.min(A.row, B.row); const r1 = Math.max(A.row, B.row);
  const c0 = Math.min(A.col, B.col); const c1 = Math.max(A.col, B.col);
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      out.push(`${colLabel(c)}${r + 1}`);
    }
  }
  return out;
}

function applyFunction(
  name: string,
  args: number[],
  ranges: { addrs: string[]; vals: number[] }[],
): number {
  const allRangeVals = ranges.flatMap((r) => r.vals);
  switch (name) {
    case 'SUM':     return allRangeVals.reduce((a, b) => a + b, 0) + args.reduce((a, b) => a + b, 0);
    case 'AVG':
    case 'AVERAGE': {
      const all = [...allRangeVals, ...args];
      if (all.length === 0) return 0;
      return all.reduce((a, b) => a + b, 0) / all.length;
    }
    case 'MIN':     return Math.min(...allRangeVals, ...args);
    case 'MAX':     return Math.max(...allRangeVals, ...args);
    case 'COUNT':   return allRangeVals.length + args.length;
    case 'PRODUCT': return [...allRangeVals, ...args].reduce((a, b) => a * b, 1);
    case 'NPV': {
      // NPV(rate, range_or_args) -- cashflows, period 1..N
      const rate = args[0] ?? 0;
      const flows = [...(args.slice(1)), ...allRangeVals];
      let v = 0;
      flows.forEach((cf, i) => { v += cf / Math.pow(1 + rate, i + 1); });
      return v;
    }
    case 'IF': {
      const cond = args[0] ?? 0;
      const thenV = args[1] ?? 0;
      const elseV = args[2] ?? 0;
      return cond ? thenV : elseV;
    }
    case 'ROUND': {
      const v = args[0] ?? 0;
      const d = args[1] ?? 0;
      const f = Math.pow(10, d);
      return Math.round(v * f) / f;
    }
    case 'ABS': return Math.abs(args[0] ?? 0);
    case 'POW': return Math.pow(args[0] ?? 0, args[1] ?? 0);
    case 'SQRT': return Math.sqrt(args[0] ?? 0);
  }
  throw new Error(`unknown function: ${name}`);
}

function evaluateRaw(addr: string, raw: string, ctx: EvalContext): number {
  ctx.resolving.add(addr);
  try {
    const expr = raw.startsWith('=') ? raw.slice(1) : raw;
    const tokens = tokenize(expr);
    const parser = new Parser(tokens, ctx);
    const v = parser.parseExpr();
    ctx.resolved[addr] = Number.isFinite(v) ? v : null;
    return Number.isFinite(v) ? v : 0;
  } finally {
    ctx.resolving.delete(addr);
  }
}

function recomputeAll(rawCells: Record<string, string>): Record<string, CellValue> {
  const ctx: EvalContext = { rawCells, resolved: {}, resolving: new Set() };
  for (const [addr, raw] of Object.entries(rawCells)) {
    if (ctx.resolved[addr] !== undefined) continue;
    if (raw === '' || raw === undefined) { ctx.resolved[addr] = null; continue; }
    if (!raw.startsWith('=')) {
      const n = Number(raw);
      if (!Number.isNaN(n) && raw.trim() !== '') {
        ctx.resolved[addr] = n;
      } else {
        ctx.resolved[addr] = raw;
      }
      continue;
    }
    try {
      evaluateRaw(addr, raw, ctx);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'error';
      ctx.resolved[addr] = msg.startsWith('cycle') ? '#CYCLE' : '#ERR';
    }
  }
  return ctx.resolved;
}

// =====================================================================
// Number formatting for display
// =====================================================================
function formatValue(v: CellValue): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (!Number.isFinite(v)) return '';
  // Prefer compact decimal; switch to scientific for very large / small
  const abs = Math.abs(v);
  if (abs !== 0 && (abs >= 1e9 || abs < 1e-4)) {
    return v.toExponential(3);
  }
  // 6 significant digits, but strip trailing zeros
  const n = Number(v.toPrecision(6));
  return Number.isInteger(n) ? n.toString() : n.toString();
}

// =====================================================================
// Component
// =====================================================================
const ROWS = 12;
const COLS = 5;

interface NumbersPaneProps {
  lang: 'de' | 'en';
  onChange: (payload: {
    rows: number;
    cols: number;
    cells: Record<string, { raw: string; value: CellValue }>;
    summary: string;
    cell_count: number;
  }) => void;
  onEvent: (action: 'submit' | 'clear', details?: Record<string, unknown>) => void;
  disabled?: boolean;
  lastPushedAt?: number | null;
  pendingEdit?: boolean;
}

const NumbersPane: React.FC<NumbersPaneProps> = ({
  lang,
  onChange,
  onEvent,
  disabled,
  lastPushedAt,
  pendingEdit,
}) => {
  // raw[addr] -> string the candidate typed (label, number, or formula).
  // Empty / whitespace strings are treated as empty cells.
  const [raw, setRaw] = useState<Record<string, string>>({});
  const [active, setActive] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [justPushed, setJustPushed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!lastPushedAt) return;
    setJustPushed(true);
    const t = setTimeout(() => setJustPushed(false), 1500);
    return () => clearTimeout(t);
  }, [lastPushedAt]);

  const resolved = useMemo(() => recomputeAll(raw), [raw]);

  // Build the AI-facing summary + push to parent on every change. Summary
  // is a brief bullet list of populated cells: humans plus AI both find
  // this readable.
  const cellsForBackend = useMemo(() => {
    const out: Record<string, { raw: string; value: CellValue }> = {};
    for (const [addr, r] of Object.entries(raw)) {
      if (r === undefined || r.trim() === '') continue;
      out[addr] = { raw: r, value: resolved[addr] ?? null };
    }
    return out;
  }, [raw, resolved]);

  const summary = useMemo(() => {
    const lines: string[] = [];
    const sortedAddrs = Object.keys(cellsForBackend).sort((a, b) => {
      const A = parseAddr(a); const B = parseAddr(b);
      if (!A || !B) return 0;
      if (A.row !== B.row) return A.row - B.row;
      return A.col - B.col;
    });
    for (const addr of sortedAddrs) {
      const cell = cellsForBackend[addr];
      if (!cell) continue;
      const valStr = formatValue(cell.value);
      if (cell.raw.startsWith('=')) {
        lines.push(`${addr}: ${cell.raw} -> ${valStr}`);
      } else {
        lines.push(`${addr}: ${valStr || cell.raw}`);
      }
    }
    return lines.join('\n');
  }, [cellsForBackend]);

  // Push every change to the parent — useArtifactSync handles debouncing.
  useEffect(() => {
    onChange({
      rows: ROWS,
      cols: COLS,
      cells: cellsForBackend,
      summary,
      cell_count: Object.keys(cellsForBackend).length,
    });
  }, [cellsForBackend, summary, onChange]);

  const startEdit = useCallback((addr: string) => {
    if (disabled) return;
    setActive(addr);
    setEditing(addr);
    setDraft(raw[addr] ?? '');
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [disabled, raw]);

  const commitEdit = useCallback(() => {
    if (editing) {
      setRaw((prev) => {
        const next = { ...prev };
        if (draft.trim() === '') delete next[editing];
        else next[editing] = draft;
        return next;
      });
    }
    setEditing(null);
  }, [editing, draft]);

  const cancelEdit = useCallback(() => {
    setEditing(null);
    setDraft('');
  }, []);

  const handleKey = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
      // jump to cell below
      if (active) {
        const A = parseAddr(active);
        if (A && A.row < ROWS - 1) {
          const next = `${colLabel(A.col)}${A.row + 2}`;
          setActive(next);
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      commitEdit();
      if (active) {
        const A = parseAddr(active);
        if (A && A.col < COLS - 1) {
          const next = `${colLabel(A.col + 1)}${A.row + 1}`;
          setActive(next);
        }
      }
    }
  }, [active, commitEdit, cancelEdit]);

  const handleClear = useCallback(() => {
    if (disabled) return;
    if (Object.keys(raw).length === 0) return;
    setRaw({});
    onEvent('clear');
  }, [disabled, raw, onEvent]);

  const handleSubmit = useCallback(() => {
    if (disabled) return;
    onEvent('submit', { cell_count: Object.keys(cellsForBackend).length });
  }, [disabled, cellsForBackend, onEvent]);

  const t = useMemo(() => ({
    title:        lang === 'de' ? 'Zahlen-Pane' : 'Numbers',
    submit:       lang === 'de' ? 'Rechnung absenden' : 'Submit calculation',
    submitHint:   lang === 'de'
      ? 'Sagt der KI: „Ich habe gerechnet". Sie gibt dir Feedback.'
      : 'Tells the AI "calculation done". They will respond with feedback.',
    clear:        lang === 'de' ? 'Leeren' : 'Clear',
    aiSeesPushed: lang === 'de' ? 'AI sieht deine Rechnung' : 'AI sees your numbers',
    aiSeesPending: lang === 'de' ? 'Wird an die AI gesendet ...' : 'Syncing to AI ...',
    aiSeesIdle:   lang === 'de' ? 'Tippe - die AI schaut zu' : 'Type - the AI is watching',
    cellHelp:     lang === 'de'
      ? 'Tippe Text, Zahl oder =FORMEL (z.B. =A1*B1, =SUM(A1:A5), =NPV(0.1, B1:B5))'
      : 'Type text, number or =FORMULA (e.g. =A1*B1, =SUM(A1:A5), =NPV(0.1, B1:B5))',
  }), [lang]);

  const awarenessLabel = pendingEdit
    ? t.aiSeesPending
    : lastPushedAt
      ? t.aiSeesPushed
      : t.aiSeesIdle;

  return (
    <div className="flex flex-col h-full bg-[#0d1117] rounded-2xl border border-white/10 overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-white/50" />
          <span className="text-[11px] uppercase tracking-wider text-white/55 font-semibold">
            {t.title}
          </span>
          <span
            className={[
              'ml-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium border transition-colors',
              justPushed
                ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-200'
                : pendingEdit
                  ? 'bg-amber-500/10 border-amber-400/30 text-amber-200/90'
                  : 'bg-white/[0.04] border-white/10 text-white/55',
            ].join(' ')}
            title={awarenessLabel}
          >
            <span className="relative inline-flex h-1.5 w-1.5">
              <span
                className={[
                  'absolute inline-flex h-full w-full rounded-full opacity-75',
                  justPushed
                    ? 'bg-emerald-400 animate-ping'
                    : pendingEdit
                      ? 'bg-amber-400 animate-pulse'
                      : 'bg-white/30',
                ].join(' ')}
              />
              <span
                className={[
                  'relative inline-flex h-1.5 w-1.5 rounded-full',
                  justPushed ? 'bg-emerald-400' : pendingEdit ? 'bg-amber-400' : 'bg-white/40',
                ].join(' ')}
              />
            </span>
            <Eye className="w-3 h-3" />
            {awarenessLabel}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleClear}
            disabled={disabled || Object.keys(raw).length === 0}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-white/70 bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] disabled:opacity-40"
          >
            <Trash2 className="w-3 h-3" />
            {t.clear}
          </button>
          <button
            onClick={handleSubmit}
            disabled={disabled}
            title={t.submitHint}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold text-white bg-gradient-to-r from-pink-500 to-orange-400 hover:from-pink-600 hover:to-orange-500 disabled:opacity-40"
          >
            <Send className="w-3 h-3" />
            {t.submit}
          </button>
        </div>
      </div>

      {/* Help line */}
      <div className="px-3 py-1.5 text-[10px] text-white/45 border-b border-white/5 bg-white/[0.015]">
        {t.cellHelp}
      </div>

      {/* Grid */}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full border-separate border-spacing-0 text-[12px]">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="bg-white/[0.04] text-white/40 text-[10px] font-medium border-r border-b border-white/10 w-10 px-1 py-1.5">
                &nbsp;
              </th>
              {Array.from({ length: COLS }, (_, c) => (
                <th
                  key={c}
                  className="bg-white/[0.04] text-white/45 text-[10px] font-semibold border-r border-b border-white/10 px-2 py-1.5 min-w-[100px]"
                >
                  {colLabel(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: ROWS }, (_, r) => (
              <tr key={r}>
                <td className="bg-white/[0.03] text-white/40 text-[10px] font-medium border-r border-b border-white/10 text-center w-10 px-1 py-1">
                  {r + 1}
                </td>
                {Array.from({ length: COLS }, (_, c) => {
                  const addr = `${colLabel(c)}${r + 1}`;
                  const isActive = active === addr;
                  const isEditing = editing === addr;
                  const cellRaw = raw[addr];
                  const value = resolved[addr];
                  const display = isEditing
                    ? draft
                    : cellRaw && cellRaw.startsWith('=')
                      ? formatValue(value)
                      : cellRaw ?? '';
                  return (
                    <td
                      key={c}
                      onClick={() => setActive(addr)}
                      onDoubleClick={() => startEdit(addr)}
                      className={[
                        'border-r border-b border-white/8 px-2 py-1 cursor-cell',
                        isActive
                          ? 'bg-pink-500/10 ring-1 ring-pink-400/40 outline-none'
                          : cellRaw
                            ? 'bg-white/[0.02]'
                            : 'bg-transparent hover:bg-white/[0.02]',
                      ].join(' ')}
                    >
                      {isEditing ? (
                        <input
                          ref={inputRef}
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={handleKey}
                          className="w-full bg-transparent text-white/95 outline-none text-[12px] font-mono"
                          autoFocus
                        />
                      ) : (
                        <span
                          className={[
                            'block truncate',
                            cellRaw && cellRaw.startsWith('=')
                              ? typeof value === 'number'
                                ? 'text-emerald-300/90 font-mono text-right'
                                : value === '#CYCLE' || value === '#ERR'
                                  ? 'text-rose-300/80 font-mono text-right'
                                  : 'text-white/70 font-mono'
                              : !Number.isNaN(Number(cellRaw))
                                ? 'text-white/90 font-mono text-right'
                                : 'text-white/85',
                          ].join(' ')}
                        >
                          {display || <span className="text-white/15">·</span>}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Active cell raw input for keyboard-only editing */}
      {active && !editing && (
        <div className="border-t border-white/10 px-3 py-2 bg-white/[0.02] flex items-center gap-2 text-[11px]">
          <span className="text-white/40 font-mono w-8">{active}</span>
          <input
            value={raw[active] ?? ''}
            placeholder={lang === 'de' ? 'Wert oder Formel ...' : 'Value or formula ...'}
            onChange={(e) => {
              const v = e.target.value;
              setRaw((prev) => {
                const next = { ...prev };
                if (v.trim() === '') delete next[active];
                else next[active] = v;
                return next;
              });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const A = parseAddr(active);
                if (A && A.row < ROWS - 1) {
                  setActive(`${colLabel(A.col)}${A.row + 2}`);
                }
              }
            }}
            className="flex-1 bg-transparent border border-white/10 rounded-md px-2 py-1 text-white/90 font-mono text-[12px] outline-none focus:ring-2 focus:ring-pink-400/30"
          />
          {raw[active] && raw[active].startsWith('=') && (
            <span className="text-emerald-300/80 font-mono text-[11px]">
              = {formatValue(resolved[active])}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default NumbersPane;
